/**
 * Premium-only feature controls rendered inside the main settings panel.
 *
 * Displays the "Other Risk Factors" section (medical conditions, medications,
 * recent procedures) and a 12-hour UV forecast line chart with Today/Now/Tomorrow
 * navigation. Non-premium users see locked placeholders with upgrade prompts.
 */
import React, { useState } from 'react';
import { useAuth } from './AuthContext';
import { isPremiumVersionEnabled } from './dev-config';
import { trackEvent } from './analytics';
import UVChart from './UVChart';
import './PremiumFeatures.css';

const PremiumFeatures = ({
  uvForecast = [],
  skinType,
  duration,
  uvIndex,
  elevation = 0,
  otherFactors,
  setOtherFactors,
  graphMode = 'now',
  setGraphMode,
  graphOffset = 0,
  setGraphOffset,
  navigationLimits = { canGoLeft: false, canGoRight: false },
  onTimeSelect,
  onModeChange,
  selectedTimestamp,
  isDark = false,
}) => {
  const { isPremium: realIsPremium } = useAuth();

  // Override premium status based on dev configuration
  // If premium version is disabled, all users get premium features
  const isPremium = isPremiumVersionEnabled() ? realIsPremium : true;

  const [otherFactorsExpanded, setOtherFactorsExpanded] = useState(false);

  return (
    <>
      {/* Other Risk Factors */}
      <div className={`premium-control ${!isPremium ? 'locked' : ''}`}>
        <div className="control-header">
          <label>Other risk factors</label>
        </div>
        {isPremium ? (
          <div className="other-factors-section">
            <button
              onClick={() => { setOtherFactorsExpanded(!otherFactorsExpanded); if (!otherFactorsExpanded) trackEvent('otherRiskFactorsClicks'); }}
              style={{
                width: '100%',
                marginBottom: '12px',
                padding: '10px 20px',
                backgroundColor: otherFactorsExpanded ? '#FFD700' : '#f0f0f0',
                color: otherFactorsExpanded ? 'white' : 'black',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                transition: 'all 0.3s ease'
              }}
            >
              {otherFactorsExpanded ? 'Hide other risk factors' : 'Show other risk factors'}
            </button>
            {otherFactorsExpanded && (
              <>

                <div className="factors-categories">
                  {/* Medical Conditions */}
                  <div className="factor-category">
                    <h4 className="category-title">Medical conditions</h4>
                    <div className="category-items">
                      {Object.entries({
                        pregnant: 'Pregnant',
                        breastfeeding: 'Breastfeeding',
                        skinCancer: 'History of skin cancer',
                        rosacea: 'Rosacea',
                        eczema: 'Eczema',
                        photosensitizing: 'Photosensitizing conditions'
                      }).map(([key, label]) => (
                        <button
                          key={key}
                          onClick={() => setOtherFactors(prev => ({
                            ...prev,
                            [key]: !prev[key]
                          }))}
                          style={{
                            margin: '5px',
                            padding: '8px 15px',
                            backgroundColor: otherFactors[key] ? '#FFD700' : '#f0f0f0',
                            color: otherFactors[key] ? 'white' : 'black',
                            border: 'none',
                            borderRadius: '5px',
                            cursor: 'pointer',
                            fontSize: '13px',
                            transition: 'all 0.2s ease'
                          }}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Medications & Treatments */}
                  <div className="factor-category">
                    <h4 className="category-title">Medications & treatments</h4>
                    <div className="category-items">
                      {Object.entries({
                        acne: 'Acne medication',
                        antibiotics: 'Antibiotics (tetracyclines, fluoroquinolones, sulfa drugs)',
                        retinoids: 'Retinoids (retinol, tretinoin, isotretinoin)',
                        chemotherapy: 'Chemotherapy drugs',
                        immunosuppressants: 'Immunosuppressants',
                        diuretics: 'Diuretics (thiazides, furosemide)'
                      }).map(([key, label]) => (
                        <button
                          key={key}
                          onClick={() => setOtherFactors(prev => ({
                            ...prev,
                            [key]: !prev[key]
                          }))}
                          style={{
                            margin: '5px',
                            padding: '8px 15px',
                            backgroundColor: otherFactors[key] ? '#FFD700' : '#f0f0f0',
                            color: otherFactors[key] ? 'white' : 'black',
                            border: 'none',
                            borderRadius: '5px',
                            cursor: 'pointer',
                            fontSize: '13px',
                            transition: 'all 0.2s ease'
                          }}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Recent Procedures */}
                  <div className="factor-category">
                    <h4 className="category-title">Recent procedures</h4>
                    <div className="category-items">
                      {Object.entries({
                        tattoos: 'Recent tattoos',
                        recentProcedures: 'Recent skin procedures'
                      }).map(([key, label]) => (
                        <button
                          key={key}
                          onClick={() => setOtherFactors(prev => ({
                            ...prev,
                            [key]: !prev[key]
                          }))}
                          style={{
                            margin: '5px',
                            padding: '8px 15px',
                            backgroundColor: otherFactors[key] ? '#FFD700' : '#f0f0f0',
                            color: otherFactors[key] ? 'white' : 'black',
                            border: 'none',
                            borderRadius: '5px',
                            cursor: 'pointer',
                            fontSize: '13px',
                            transition: 'all 0.2s ease'
                          }}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="locked-message">
            <span className="lock-icon">🔒</span>
            <span>Upgrade to Premium for detailed risk factors</span>
          </div>
        )}
      </div>

      {/* UV Forecast Chart */}
      <div className={`premium-control chart-control ${!isPremium ? 'locked' : ''}`}>
        <div className="control-header">
          <label>12-hour UV window</label>
        </div>
        {isPremium ? (
          <UVChart
            chartData={uvForecast}
            graphMode={graphMode}
            setGraphMode={setGraphMode}
            graphOffset={graphOffset}
            setGraphOffset={setGraphOffset}
            navigationLimits={navigationLimits}
            onTimeSelect={onTimeSelect}
            onModeChange={onModeChange}
            selectedTimestamp={selectedTimestamp}
            isDark={isDark}
          />
        ) : (
          <div className="forecast-placeholder locked">
            <div className="locked-message">
              <span className="lock-icon">🔒</span>
              <span>Upgrade to Premium to see UV trend data</span>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default PremiumFeatures;
